// ===== DAILY REVENUE / STAFFING GOALS =====
// Target revenue per staff member per 8-hour work day. Used by the Dispatch
// Daily Goal Tracker to recommend staffing and gauge productivity.
export const REVENUE_GOAL_PER_STAFF = 400;
export const HOURS_PER_STAFF_DAY = 8;
// Implied revenue per labor hour ($50): what an hour of clocked time should produce.
export const REVENUE_GOAL_PER_HOUR = REVENUE_GOAL_PER_STAFF / HOURS_PER_STAFF_DAY;

// ===== OFFICE LOCATION =====
export const OFFICE = {
  name: 'Two Sisters Office',
  address: '2114 Angus Rd',
  city: 'Charlottesville',
  state: 'VA',
  zip: '22901',
  fullAddress: '2114 Angus Rd, Charlottesville, VA 22901',
  lat: 38.0495,
  lng: -78.5190,
  phone: '(434) 555-1234'
};

// ===== JOB STATUS CONFIG =====
export const jobStatuses = {
  scheduled: { 
    label: 'Scheduled', 
    color: '#b8926a', 
    bg: '#faf6f1',
    description: 'Job is scheduled'
  },
  en_route: { 
    label: 'En Route', 
    color: '#8b5cf6', 
    bg: '#f5f3ff',
    description: 'Team is on the way'
  },
  in_progress: { 
    label: 'In Progress', 
    color: '#2563eb', 
    bg: '#eff6ff',
    description: 'Team is currently cleaning'
  },
  completed: { 
    label: 'Completed', 
    color: '#16a34a', 
    bg: '#f0fdf4',
    description: 'Job finished'
  },
  cancelled: { 
    label: 'Cancelled', 
    color: '#dc2626', 
    bg: '#fef2f2',
    description: 'Job was cancelled'
  }
};

// ===== SERVICE TYPES =====
export const serviceTypes = {
  weekly: { label: 'Weekly', color: '#2c5f4e', icon: '🔄' },
  biweekly: { label: 'Bi-Weekly', color: '#2c5f4e', icon: '🔄' },
  monthly: { label: 'Monthly', color: '#2c5f4e', icon: '📅' },
  deep_clean: { label: 'Deep Clean', color: '#b8926a', icon: '✨' },
  move_in_out: { label: 'Move In/Out', color: '#1a2634', icon: '📦' },
  one_time: { label: 'One-Time', color: '#6b7280', icon: '1️⃣' },
  as_needed: { label: 'As Needed', color: '#6b7280', icon: '📋' },
  organizing: { label: 'Organizing', color: '#7c3aed', icon: '🗂️' },
  hoarding: { label: 'Hoarding', color: '#dc2626', icon: '⚠️' },
  junk_removal: { label: 'Junk Removal', color: '#ea580c', icon: '🚛' },
  laundry_pickup: { label: 'Laundry Pickup', color: '#8b5cf6', icon: '🧺' },
  recurring: { label: 'Recurring', color: '#2c5f4e', icon: '🔄' },
  commercial: { label: 'Commercial', color: '#0369a1', icon: '🏢' }
};

// ===== SAMPLE JOBS =====
export const sampleJobs = [
  {
    id: 'job1',
    clientId: 'client1',
    propertyId: 'prop1',
    estimateId: 'est1',
    clientName: 'Sarah Johnson',
    address: '123 Oak Street',
    city: 'Charlottesville',
    state: 'VA',
    zip: '22901',
    fullAddress: '123 Oak Street, Charlottesville, VA 22901',
    status: 'in_progress',
    date: '2026-01-23',
    scheduledTime: '09:00',
    scheduledEndTime: '12:00',
    duration: 3,
    actual_start: '2026-01-23T09:05:00Z',
    team: ['emp1', 'emp2'],
    teamNames: ['Ashanti', 'Brandi'],
    serviceType: 'biweekly',
    price: 210,
    isFirstClean: false,
    routeOrder: 1,
    lat: 38.0293,
    lng: -78.4767,
    driveTimeFromPrev: 12,
    driveDistanceFromPrev: 4.2,
    notes: 'Gate code: 1234. Dog is friendly but loud.',
    clientPhone: '(434) 555-1001',
    createdAt: '2026-01-20T10:00:00Z',
    updatedAt: '2026-01-23T09:05:00Z'
  },
  {
    id: 'job2',
    clientId: 'client5',
    propertyId: 'prop6',
    estimateId: 'est2',
    clientName: 'The Hendersons',
    address: '456 Maple Ave',
    city: 'Charlottesville',
    state: 'VA',
    zip: '22902',
    fullAddress: '456 Maple Ave, Charlottesville, VA 22902',
    status: 'scheduled',
    date: '2026-01-23',
    scheduledTime: '13:00',
    scheduledEndTime: '17:00',
    duration: 4,
    team: ['emp3', 'emp4'],
    teamNames: ['Elaine', 'Sam'],
    serviceType: 'deep_clean',
    price: 485,
    isFirstClean: true,
    routeOrder: 1,
    lat: 38.0350,
    lng: -78.4850,
    driveTimeFromPrev: 15,
    driveDistanceFromPrev: 5.8,
    notes: 'First clean - do full walkthrough. Client works from home.',
    clientPhone: '(434) 555-1002',
    createdAt: '2026-01-20T11:00:00Z',
    updatedAt: '2026-01-20T11:00:00Z'
  },
  {
    id: 'job3',
    clientId: 'client2',
    propertyId: 'prop2',
    estimateId: null,
    clientName: 'Dr. Michael Chen',
    address: '789 University Blvd',
    city: 'Charlottesville',
    state: 'VA',
    zip: '22903',
    fullAddress: '789 University Blvd, Charlottesville, VA 22903',
    status: 'scheduled',
    date: '2026-01-23',
    scheduledTime: '13:30',
    scheduledEndTime: '16:00',
    duration: 2.5,
    team: ['emp1', 'emp2'],
    teamNames: ['Ashanti', 'Brandi'],
    serviceType: 'weekly',
    price: 175,
    isFirstClean: false,
    routeOrder: 2,
    lat: 38.0336,
    lng: -78.5080,
    driveTimeFromPrev: 18,
    driveDistanceFromPrev: 6.1,
    notes: 'Key under mat. Please text when done.',
    clientPhone: '(434) 555-1003',
    createdAt: '2026-01-15T09:00:00Z',
    updatedAt: '2026-01-15T09:00:00Z'
  },
  {
    id: 'job4',
    clientId: 'client3',
    propertyId: 'prop3',
    estimateId: null,
    clientName: 'Emily Watson',
    address: '321 Pine Road',
    city: 'Crozet',
    state: 'VA',
    zip: '22932',
    fullAddress: '321 Pine Road, Crozet, VA 22932',
    status: 'completed',
    date: '2026-01-23',
    scheduledTime: '08:00',
    scheduledEndTime: '11:00',
    duration: 3,
    actual_start: '2026-01-23T08:10:00Z',
    actual_end: '2026-01-23T11:35:00Z',
    actual_duration_min: 205,
    team: ['emp3', 'emp4'],
    teamNames: ['Elaine', 'Sam'],
    serviceType: 'biweekly',
    price: 195,
    isFirstClean: false,
    routeOrder: 1,
    lat: 38.0700,
    lng: -78.7000,
    driveTimeFromPrev: 22,
    driveDistanceFromPrev: 12.4,
    notes: 'Alarm code: 5678#',
    clientPhone: '(434) 555-1004',
    completedAt: '2026-01-23T11:35:00Z',
    geofenceVerified: true,
    createdAt: '2026-01-10T14:00:00Z',
    updatedAt: '2026-01-23T11:35:00Z'
  },
  {
    id: 'job5',
    clientId: 'client4',
    propertyId: 'prop4',
    estimateId: null,
    clientName: 'Robert Martinez',
    address: '555 River Lane',
    city: 'Charlottesville',
    state: 'VA',
    zip: '22901',
    fullAddress: '555 River Lane, Charlottesville, VA 22901',
    status: 'scheduled',
    date: '2026-01-23',
    scheduledTime: '12:00',
    scheduledEndTime: '14:00',
    duration: 2,
    team: ['emp1', 'emp2'],
    teamNames: ['Ashanti', 'Brandi'],
    serviceType: 'monthly',
    price: 320,
    isFirstClean: false,
    routeOrder: 2,
    lat: 38.0400,
    lng: -78.4900,
    driveTimeFromPrev: 25,
    driveDistanceFromPrev: 14.2,
    notes: 'Park in driveway, not street.',
    clientPhone: '(434) 555-1005',
    createdAt: '2026-01-05T10:00:00Z',
    updatedAt: '2026-01-05T10:00:00Z'
  },
  // Yesterday's completed jobs (for historical data)
  {
    id: 'job8',
    clientId: 'client1',
    propertyId: 'prop1',
    estimateId: null,
    clientName: 'Lisa Thompson',
    address: '100 Cedar Court',
    city: 'Charlottesville',
    state: 'VA',
    zip: '22901',
    fullAddress: '100 Cedar Court, Charlottesville, VA 22901',
    status: 'completed',
    date: '2026-01-22',
    scheduledTime: '09:00',
    scheduledEndTime: '11:30',
    duration: 2.5,
    actual_start: '2026-01-22T09:00:00Z',
    actual_end: '2026-01-22T11:20:00Z',
    actual_duration_min: 140,
    team: ['emp1', 'emp2'],
    teamNames: ['Ashanti', 'Brandi'],
    serviceType: 'biweekly',
    price: 185,
    isFirstClean: false,
    routeOrder: 1,
    lat: 38.0310,
    lng: -78.4820,
    driveTimeFromPrev: 10,
    driveDistanceFromPrev: 3.5,
    notes: '',
    clientPhone: '(434) 555-2001',
    completedAt: '2026-01-22T11:20:00Z',
    geofenceVerified: true,
    createdAt: '2026-01-15T10:00:00Z',
    updatedAt: '2026-01-22T11:20:00Z'
  },
  {
    id: 'job9',
    clientId: 'client2',
    propertyId: 'prop2',
    estimateId: null,
    clientName: 'James Wilson',
    address: '250 Oak Hill Drive',
    city: 'Charlottesville',
    state: 'VA',
    zip: '22903',
    fullAddress: '250 Oak Hill Drive, Charlottesville, VA 22903',
    status: 'completed',
    date: '2026-01-22',
    scheduledTime: '12:30',
    scheduledEndTime: '15:00',
    duration: 2.5,
    actual_start: '2026-01-22T12:45:00Z',
    actual_end: '2026-01-22T15:30:00Z',
    actual_duration_min: 165,
    team: ['emp1', 'emp2'],
    teamNames: ['Ashanti', 'Brandi'],
    serviceType: 'weekly',
    price: 165,
    isFirstClean: false,
    routeOrder: 2,
    lat: 38.0340,
    lng: -78.5020,
    driveTimeFromPrev: 15,
    driveDistanceFromPrev: 5.2,
    notes: 'Client prefers unscented products',
    clientPhone: '(434) 555-2002',
    completedAt: '2026-01-22T15:30:00Z',
    geofenceVerified: true,
    createdAt: '2026-01-15T10:00:00Z',
    updatedAt: '2026-01-22T15:30:00Z'
  },
  {
    id: 'job10',
    clientId: 'client3',
    propertyId: 'prop3',
    estimateId: null,
    clientName: 'Karen Davis',
    address: '400 Mountain View',
    city: 'Crozet',
    state: 'VA',
    zip: '22932',
    fullAddress: '400 Mountain View, Crozet, VA 22932',
    status: 'completed',
    date: '2026-01-22',
    scheduledTime: '09:00',
    scheduledEndTime: '13:00',
    duration: 4,
    actual_start: '2026-01-22T09:15:00Z',
    actual_end: '2026-01-22T12:45:00Z',
    actual_duration_min: 210,
    team: ['emp3', 'emp4'],
    teamNames: ['Elaine', 'Sam'],
    serviceType: 'deep_clean',
    price: 395,
    isFirstClean: true,
    routeOrder: 1,
    lat: 38.0680,
    lng: -78.6950,
    driveTimeFromPrev: 20,
    driveDistanceFromPrev: 11.0,
    notes: 'First deep clean. 3 dogs.',
    clientPhone: '(434) 555-2003',
    completedAt: '2026-01-22T12:45:00Z',
    geofenceVerified: true,
    createdAt: '2026-01-18T10:00:00Z',
    updatedAt: '2026-01-22T12:45:00Z'
  },
  {
    id: 'job11',
    clientId: 'client4',
    propertyId: 'prop4',
    estimateId: null,
    clientName: 'Mark Anderson',
    address: '600 Brookside Lane',
    city: 'Charlottesville',
    state: 'VA',
    zip: '22901',
    fullAddress: '600 Brookside Lane, Charlottesville, VA 22901',
    status: 'completed',
    date: '2026-01-22',
    scheduledTime: '14:00',
    scheduledEndTime: '16:30',
    duration: 2.5,
    actual_start: '2026-01-22T14:10:00Z',
    actual_end: '2026-01-22T16:20:00Z',
    actual_duration_min: 130,
    team: ['emp3', 'emp4'],
    teamNames: ['Elaine', 'Sam'],
    serviceType: 'biweekly',
    price: 195,
    isFirstClean: false,
    routeOrder: 2,
    lat: 38.0420,
    lng: -78.4880,
    driveTimeFromPrev: 18,
    driveDistanceFromPrev: 7.5,
    notes: '',
    clientPhone: '(434) 555-2004',
    completedAt: '2026-01-22T16:20:00Z',
    geofenceVerified: true,
    createdAt: '2026-01-10T10:00:00Z',
    updatedAt: '2026-01-22T16:20:00Z'
  },
  // Tomorrow's jobs
  {
    id: 'job6',
    clientId: 'client1',
    propertyId: 'prop1',
    estimateId: null,
    clientName: 'James Miller',
    address: '888 Sunset Drive',
    city: 'Charlottesville',
    state: 'VA',
    zip: '22901',
    fullAddress: '888 Sunset Drive, Charlottesville, VA 22901',
    status: 'scheduled',
    date: '2026-01-24',
    scheduledTime: '09:00',
    scheduledEndTime: '12:30',
    duration: 3.5,
    team: ['emp1', 'emp2'],
    teamNames: ['Ashanti', 'Brandi'],
    serviceType: 'biweekly',
    price: 225,
    isFirstClean: false,
    routeOrder: 1,
    lat: 38.0250,
    lng: -78.4650,
    driveTimeFromPrev: 10,
    driveDistanceFromPrev: 3.5,
    notes: '',
    clientPhone: '(434) 555-1006',
    createdAt: '2026-01-12T09:00:00Z',
    updatedAt: '2026-01-12T09:00:00Z'
  },
  // Unassigned job for today (needs team assignment)
  {
    id: 'job7',
    clientId: 'client6',
    propertyId: 'prop7',
    estimateId: 'est3',
    clientName: 'Patricia Adams',
    address: '222 Meadow Lane',
    city: 'Charlottesville',
    state: 'VA',
    zip: '22901',
    fullAddress: '222 Meadow Lane, Charlottesville, VA 22901',
    status: 'scheduled',
    date: '2026-01-23',
    scheduledTime: '14:00',
    scheduledEndTime: '17:00',
    duration: 3,
    team: [],
    teamNames: [],
    serviceType: 'deep_clean',
    price: 425,
    isFirstClean: true,
    routeOrder: 0,
    lat: 38.0420,
    lng: -78.4720,
    driveTimeFromPrev: 0,
    driveDistanceFromPrev: 0,
    notes: 'New client - First deep clean. Has 2 cats.',
    clientPhone: '(434) 555-1007',
    createdAt: '2026-01-22T15:00:00Z',
    updatedAt: '2026-01-22T15:00:00Z'
  }
];

// ===== HELPER FUNCTIONS =====

// Format time for display (24h to 12h)
export const formatJobTime = (time24) => {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

// Format duration
export const formatDuration = (hours) => {
  if (hours === 1) return '1 hr';
  if (hours % 1 === 0) return `${hours} hrs`;
  return `${hours} hrs`;
};

// Get jobs for a specific date
export const getJobsForDate = (jobs, date) => {
  return jobs.filter(job => job.date === date);
};

// Group jobs by team
export const groupJobsByTeam = (jobs) => {
  const grouped = {};
  jobs.forEach(job => {
    // Use sorted team IDs for consistent grouping (IDs are more reliable than names)
    const teamIds = job.team || [];
    const teamKey = teamIds.length > 0 
      ? [...teamIds].sort().join('_') 
      : 'unassigned';
    
    if (!grouped[teamKey]) {
      grouped[teamKey] = {
        teamKey: job.teamNames?.join(' & ') || 'Unassigned',
        teamIds: job.team || [],
        teamNames: job.teamNames || [],
        jobs: []
      };
    }
    grouped[teamKey].jobs.push(job);
  });
  
  // Sort jobs within each team by scheduled time
  Object.values(grouped).forEach(team => {
    team.jobs.sort((a, b) => {
      // First by route order if set
      if (a.routeOrder !== undefined && b.routeOrder !== undefined) {
        return a.routeOrder - b.routeOrder;
      }
      // Then by scheduled time
      return (a.scheduledTime || '').localeCompare(b.scheduledTime || '');
    });
  });
  
  return grouped;
};

// Calculate route statistics
export const calculateRouteStats = (jobs) => {
  const totalJobTime = jobs.reduce((sum, j) => sum + j.duration, 0);
  const totalDriveTime = jobs.reduce((sum, j) => sum + (j.driveTimeFromPrev || 0), 0);
  const returnDriveTime = jobs.length > 0 ? Math.round((jobs[jobs.length - 1].driveTimeFromPrev || 10) * 0.8) : 0;
  const totalMiles = jobs.reduce((sum, j) => sum + (j.driveDistanceFromPrev || 0), 0);
  const returnMiles = jobs.length > 0 ? Math.round((jobs[jobs.length - 1].driveDistanceFromPrev || 5) * 0.8 * 10) / 10 : 0;
  const revenue = jobs.reduce((sum, j) => sum + (j.price || 0), 0);
  
  return {
    totalJobTime,
    totalDriveTime: totalDriveTime + returnDriveTime,
    totalMiles: Math.round((totalMiles + returnMiles) * 10) / 10,
    revenue: Math.round(revenue * 100) / 100, // Round to cents
    jobCount: jobs.length
  };
};

// Calculate shift start/end times for a team
export const calculateShiftTimes = (jobs) => {
  if (!jobs || jobs.length === 0) return null;
  
  // Sort jobs by scheduled time
  const sortedJobs = [...jobs].sort((a, b) => a.scheduledTime.localeCompare(b.scheduledTime));
  
  const firstJob = sortedJobs[0];
  const lastJob = sortedJobs[sortedJobs.length - 1];
  
  // Parse first job time
  const [firstHour, firstMin] = firstJob.scheduledTime.split(':').map(Number);
  const firstDriveTime = firstJob.driveTimeFromPrev || 15; // default 15 min if not set
  
  // Shift start = 15 min before first drive time
  // So: first job time - drive time - 15 min prep
  const shiftStartMinutes = (firstHour * 60 + firstMin) - firstDriveTime - 15;
  const startHour = Math.floor(shiftStartMinutes / 60);
  const startMin = shiftStartMinutes % 60;
  
  // Parse last job end time
  const [lastEndHour, lastEndMin] = lastJob.scheduledEndTime.split(':').map(Number);
  const returnDriveTime = Math.round((lastJob.driveTimeFromPrev || 15) * 0.8); // estimate return
  
  // Shift end = last job end + return drive time
  const shiftEndMinutes = (lastEndHour * 60 + lastEndMin) + returnDriveTime;
  const endHour = Math.floor(shiftEndMinutes / 60);
  const endMin = shiftEndMinutes % 60;
  
  // Calculate total shift hours
  const totalShiftMinutes = shiftEndMinutes - shiftStartMinutes;
  const totalShiftHours = Math.round(totalShiftMinutes / 60 * 10) / 10;
  
  // Format times
  const formatTime = (h, m) => {
    const hour12 = h % 12 || 12;
    const ampm = h >= 12 ? 'PM' : 'AM';
    return `${hour12}:${m.toString().padStart(2, '0')} ${ampm}`;
  };
  
  return {
    shiftStart: formatTime(startHour, startMin),
    shiftEnd: formatTime(endHour, endMin),
    totalShiftHours,
    firstJobTime: formatJobTime(firstJob.scheduledTime),
    lastJobEnd: formatJobTime(lastJob.scheduledEndTime),
    prepTime: 15,
    firstDriveTime,
    returnDriveTime
  };
};

// Generate Google Maps URL for full route
export const getGoogleMapsRouteUrl = (jobs) => {
  if (jobs.length === 0) return '#';
  const waypoints = jobs.map(j => encodeURIComponent(j.fullAddress)).join('|');
  return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(OFFICE.fullAddress)}&destination=${encodeURIComponent(OFFICE.fullAddress)}&waypoints=${waypoints}&travelmode=driving`;
};

// Generate Google Maps URL for single destination
export const getGoogleMapsDirectionsUrl = (address) => {
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}&travelmode=driving`;
};
